/**
 * AppICEMAP — QuickReportSheet Component
 * Multi-step report creation flow.
 *
 * Steps: 1. Category → 2. Location → 3. Photo (optional) → 4. Details + Privacy
 *
 * Tag: <quick-report-sheet>
 *
 * Dispatches:
 *   quick-report:submit    → { reportPayload }
 *   quick-report:close     → { }
 */

import { state } from "../../store/index.js";
import { createReport, linkReportPhotos } from "../../api/reports.js";
import {
	uploadPhoto,
	validateImageFile,
	SUPPORTED_IMAGE_TYPES,
} from "../../utils/image.js";
import { show as showToast } from "../shared/Toast.js";
import L from "leaflet";
import {
	searchAddress,
	reverseGeocode,
	getCountryCode,
} from "../../services/geocode.js";
import { TILE_URL_DARK, TILE_ATTRIBUTION } from "../../utils/basemap.js";
import { isInUS, US_MAP_BOUNDS, US_DEFAULT_CENTER } from "../../utils/geo.js";
import {
	getCategoryStyle,
	categoryIconHtml,
	categoryTitle,
} from "../../utils/categoryStyle.js";

/** Máximo de caracteres de la descripción */
const MAX_DESCRIPTION = 500;

/** A partir de cuántos caracteres restantes se avisa */
const AVISO_RESTANTES = 50;

export class QuickReportSheet extends HTMLElement {
	constructor() {
		super();
		this._open = false;
		this._step = 1;

		// Form data
		this._category = null;
		this._location = null; // { lat, lng, display_name }
		this._locationSource = "gps"; // "gps" | "search"
		this._photoFile = null;
		this._photoPreview = null;
		this._title = "";
		this._description = "";
		this._locationPrecision = "exact";
		this._displayNameMode = "real";
		this._isSubmitting = false;
		this._error = null;
		this._iceWarningAccepted = false;
	}

	connectedCallback() {
		this._render();
		this._bindEvents();
	}

	/* ── Public API ────────────────────────────────────────────────────────── */

	get open() {
		return this._open;
	}
	set open(val) {
		this._open = !!val;
		if (this._open) {
			this._reset();
			this._show();
		} else {
			this._hide();
		}
	}

	/* ── Render ────────────────────────────────────────────────────────────── */

	_render() {
		this.innerHTML = `
      <div class="quick-report">
        <div class="quick-report__header">
          <button class="quick-report__close" aria-label="Cerrar">
            <i class="ph ph-x" aria-hidden="true"></i>
          </button>
          <h2>Nuevo reporte</h2>
          <div style="width:40px"></div><!-- spacer -->
        </div>

        <div class="quick-report__steps">
          <span class="quick-report__step-dot quick-report__step-dot--active" data-step="1"></span>
          <span class="quick-report__step-line"></span>
          <span class="quick-report__step-dot" data-step="2"></span>
          <span class="quick-report__step-line"></span>
          <span class="quick-report__step-dot" data-step="3"></span>
          <span class="quick-report__step-line"></span>
          <span class="quick-report__step-dot" data-step="4"></span>
        </div>

        <div class="quick-report__body"></div>
        <div class="quick-report__footer"></div>
      </div>
      <ice-privacy-warning></ice-privacy-warning>
    `;

		this._sheetEl = this.querySelector(".quick-report");
		this._bodyEl = this.querySelector(".quick-report__body");
		this._footerEl = this.querySelector(".quick-report__footer");
		this._stepsEl = this.querySelector(".quick-report__steps");
		this._iceWarning = this.querySelector("ice-privacy-warning");
	}

	/* ── Events ────────────────────────────────────────────────────────────── */

	_bindEvents() {
		const closeBtn = this.querySelector(".quick-report__close");
		if (closeBtn) {
			closeBtn.addEventListener("click", () => this._close());
		}

		// ICE privacy warning
		if (this._iceWarning) {
			this._iceWarning.addEventListener("ice-privacy:accept", () => {
				this._iceWarningAccepted = true;
				this._goToStep(4);
			});
			this._iceWarning.addEventListener("ice-privacy:cancel", () => {
				// Stay on step 2
			});
		}
	}

	/* ── Show / Hide ───────────────────────────────────────────────────────── */

	_show() {
		if (this._sheetEl) {
			this._sheetEl.classList.add("quick-report--open");
			document.body.style.overflow = "hidden";
		}
		this._goToStep(1);
	}

	_hide() {
		if (this._picker) {
			this._picker.remove();
			this._picker = null;
		}
		if (this._sheetEl) {
			this._sheetEl.classList.remove("quick-report--open");
			document.body.style.overflow = "";
		}
	}

	_close() {
		this.open = false;
		this.dispatchEvent(
			new CustomEvent("quick-report:close", { bubbles: true }),
		);
	}

	/* ── Reset ─────────────────────────────────────────────────────────────── */

	_reset() {
		this._step = 1;
		this._category = null;
		this._location = null;
		this._locationSource = "gps";
		this._photoFile = null;
		this._photoPreview = null;
		this._title = "";
		this._description = "";
		this._locationPrecision = "exact";
		this._displayNameMode = "real";
		this._isSubmitting = false;
		this._error = null;
		this._iceWarningAccepted = false;
	}

	/* ── Step Navigation ───────────────────────────────────────────────────── */

	_goToStep(step) {
		this._step = step;
		this._updateStepDots();
		this._renderStep();
	}

	_updateStepDots() {
		const dots = this._stepsEl?.querySelectorAll(".quick-report__step-dot");
		if (!dots) return;

		for (const dot of dots) {
			const s = parseInt(dot.dataset.step, 10);
			dot.classList.remove(
				"quick-report__step-dot--active",
				"quick-report__step-dot--done",
			);
			if (s < this._step) dot.classList.add("quick-report__step-dot--done");
			if (s === this._step) dot.classList.add("quick-report__step-dot--active");
		}
	}

	_renderStep() {
		if (!this._bodyEl || !this._footerEl) return;

		this._bodyEl.innerHTML = "";
		this._footerEl.innerHTML = "";

		switch (this._step) {
			case 1:
				this._renderStep1();
				break;
			case 2:
				this._renderStep2();
				break;
			case 3:
				this._renderStep3();
				break;
			case 4:
				this._renderStep4();
				break;
			case 5:
				this._renderStep5();
				break;
		}
	}

	/* ── Step 1: Category Grid ─────────────────────────────────────────────── */

	_renderStep1() {
		const categories = (state.categories || []).filter(
			(c) => c.is_active !== false,
		);

		let html =
			'<div class="category-grid" role="radiogroup" aria-label="Seleccionar categoría">';

		for (const cat of categories) {
			const { color, ink } = getCategoryStyle(cat);
			const selected = this._category?.id === cat.id;
			html += `
        <button class="category-tile${selected ? " category-tile--selected" : ""}"
                role="radio" aria-checked="${selected}"
                data-category-id="${cat.id}"
                data-category-slug="${escapeAttr(cat.slug)}">
          <div class="category-tile__icon" style="background:${color};color:${ink}">
            ${categoryIconHtml(cat)}
          </div>
          <span class="category-tile__label">${escapeHtml(cat.label)}</span>
        </button>
      `;
		}

		html += "</div>";

		this._bodyEl.innerHTML = html;

		// Bind category selection
		this._bodyEl.addEventListener("click", (e) => {
			const tile = e.target.closest(".category-tile");
			if (!tile) return;

			const catId = tile.dataset.categoryId;
			this._category = categories.find((c) => c.id === catId) || null;

			// Update visual selection
			for (const t of this._bodyEl.querySelectorAll(".category-tile")) {
				t.classList.toggle(
					"category-tile--selected",
					t.dataset.categoryId === catId,
				);
				t.setAttribute(
					"aria-checked",
					t.dataset.categoryId === catId ? "true" : "false",
				);
			}

			// Re-enable the Continue button now that a category is chosen
			const contBtn = this._footerEl?.querySelector(".btn");
			if (contBtn) contBtn.disabled = !this._category;
		});

		// Footer: Continue button
		this._footerEl.innerHTML = `
      <button class="btn btn--primary btn--block" ${this._category ? "" : "disabled"}>
        Continuar
      </button>
    `;

		const continueBtn = this._footerEl.querySelector(".btn");
		if (continueBtn) {
			continueBtn.addEventListener("click", () => {
				if (this._category) {
					this._goToStep(2);
				}
			});
		}
	}

	/* ── Step 2: Location Search ──────────────────────────────────────────────── */

	_renderStep2() {
		const hasLocation =
			(this._locationSource === "search" || this._locationSource === "map") &&
			this._location;

		this._bodyEl.innerHTML = `
    			<div class="location-step">
    				<p class="location-step__hint">
    					${hasLocation ? "Ubicación seleccionada" : "Busca la dirección del incidente"}
    				</p>

    				<div class="location-search">
    					<i class="ph ph-magnifying-glass location-search__icon" aria-hidden="true"></i>
    					<input type="text"
    						class="input location-search__input"
    						id="location-search-input"
    						placeholder="Ej: 111 Herrod Blvd, NJ 08810"
    						autocomplete="off">
    				</div>

    				<div class="location-suggestions" id="location-suggestions"></div>

    				<button class="btn btn--ghost location-step__pick" id="btn-pick-map">
    					<i class="ph ph-map-trifold" aria-hidden="true"></i>
    					Marcar en el mapa
    				</button>

    				${
							hasLocation
								? `<div class="location-selected">
    								<i class="ph ph-map-pin" aria-hidden="true"></i>
    								<span>${escapeHtml(this._location.display_name)}</span>
    								<button class="btn btn--ghost btn--sm" id="btn-clear-location">Cambiar</button>
    							</div>`
								: `<p class="location-step__fallback">
    								Si no buscas una dirección, usaremos tu ubicación GPS actual.
    							</p>`
						}
    			</div>
    		`;

		// Debounced search
		let searchTimer;
		const searchInput = this._bodyEl.querySelector("#location-search-input");
		const suggestionsEl = this._bodyEl.querySelector("#location-suggestions");

		if (searchInput) {
			searchInput.addEventListener("input", () => {
				clearTimeout(searchTimer);
				const query = searchInput.value.trim();
				if (query.length < 3) {
					suggestionsEl.innerHTML = "";
					return;
				}
				searchTimer = setTimeout(() => this._searchLocation(query), 350);
			});

			// Auto-focus for better UX
			requestAnimationFrame(() => searchInput.focus());
		}

		// Close suggestions on outside click
		const closeSuggestions = (e) => {
			if (
				!e.target.closest(".location-search") &&
				!e.target.closest(".location-suggestions")
			) {
				suggestionsEl.innerHTML = "";
			}
		};
		this._bodyEl.addEventListener("click", closeSuggestions);

		// Pick on the map
		this._bodyEl
			.querySelector("#btn-pick-map")
			?.addEventListener("click", () => this._openMapPicker());

		// Clear location
		this._bodyEl
			.querySelector("#btn-clear-location")
			?.addEventListener("click", () => {
				this._location = null;
				this._locationSource = "gps";
				this._renderStep2();
			});

		// Footer
		this._footerEl.innerHTML = `
    			<button class="btn btn--ghost" id="btn-back-step2">Atrás</button>
    			<button class="btn btn--primary" id="btn-next-step2">
    				Continuar
    			</button>
    		`;

		this._footerEl
			.querySelector("#btn-back-step2")
			?.addEventListener("click", () => {
				this._goToStep(1);
			});

		this._footerEl
			.querySelector("#btn-next-step2")
			?.addEventListener("click", () => {
				this._goToStep(3);
			});
	}

	async _searchLocation(query) {
		const suggestionsEl = this._bodyEl?.querySelector("#location-suggestions");
		if (!suggestionsEl) return;

		suggestionsEl.innerHTML =
			'<div class="location-suggestions__loading">Buscando...</div>';

		// searchAddress throttles Nominatim and retries with looser queries when
		// the exact string finds nothing (a locality OSM files differently, etc.)
		const results = await searchAddress(query);

		// The step may have been re-rendered while the request was in flight
		if (!this._bodyEl?.contains(suggestionsEl)) return;

		if (results.length === 0) {
			suggestionsEl.innerHTML = `
				<div class="location-suggestions__empty">
					No encontramos esa dirección. Prueba con menos detalle
					(calle y código postal) o márcala en el mapa.
				</div>`;
			return;
		}

		suggestionsEl.innerHTML = results
			.map(
				(r) => `
				<button class="location-suggestion"
					data-lat="${r.lat}"
					data-lng="${r.lng}"
					data-name="${escapeAttr(r.label)}">
					<i class="ph ph-map-pin" aria-hidden="true"></i>
					<span>${escapeHtml(r.label)}</span>
				</button>`,
			)
			.join("");

		// Bind selection
		suggestionsEl.querySelectorAll(".location-suggestion").forEach((btn) => {
			btn.addEventListener("click", () => {
				this._location = {
					lat: parseFloat(btn.dataset.lat),
					lng: parseFloat(btn.dataset.lng),
					display_name: btn.dataset.name,
				};
				this._locationSource = "search";
				this._renderStep2();
			});
		});
	}

	/* ── Map picker ────────────────────────────────────────────────────────── */

	/**
	 * Full-screen map to place the report by hand, for when the address search
	 * comes up empty. The pin stays fixed at the centre and the user moves the
	 * map under it — far easier to aim on a phone than tapping an exact point.
	 */
	_openMapPicker() {
		if (this._picker) return;

		const start = this._location ||
			(Array.isArray(state.ui?.userLocation)
				? { lat: state.ui.userLocation[0], lng: state.ui.userLocation[1] }
				: null) || { lat: US_DEFAULT_CENTER[0], lng: US_DEFAULT_CENTER[1] };

		const overlay = document.createElement("div");
		overlay.className = "map-picker";
		overlay.innerHTML = `
			<div class="map-picker__header">
				<button class="map-picker__close" aria-label="Cancelar">
					<i class="ph ph-x" aria-hidden="true"></i>
				</button>
				<h3>Marca el lugar</h3>
				<div style="width:40px"></div>
			</div>
			<div class="map-picker__map"></div>
			<div class="map-picker__crosshair" aria-hidden="true">
				<i class="ph ph-map-pin"></i>
			</div>
			<div class="map-picker__footer">
				<p class="map-picker__address" id="map-picker-address">Mueve el mapa para situar el punto</p>
				<button class="btn btn--primary btn--block" id="map-picker-confirm">
					Usar esta ubicación
				</button>
			</div>
		`;
		document.body.appendChild(overlay);
		this._picker = overlay;

		const map = L.map(overlay.querySelector(".map-picker__map"), {
			center: isInUS(start.lat, start.lng)
				? [start.lat, start.lng]
				: US_DEFAULT_CENTER,
			zoom: isInUS(start.lat, start.lng) ? 17 : 4,
			zoomControl: false,
			attributionControl: true,
			maxBounds: US_MAP_BOUNDS,
			maxBoundsViscosity: 0.8,
		});
		L.tileLayer(TILE_URL_DARK, {
			attribution: TILE_ATTRIBUTION,
			maxZoom: 19,
		}).addTo(map);
		L.control.zoom({ position: "topleft" }).addTo(map);

		const addressEl = overlay.querySelector("#map-picker-address");
		let picked = { ...start, display_name: "" };

		const describeCentre = async () => {
			const c = map.getCenter();
			picked = { lat: c.lat, lng: c.lng, display_name: picked.display_name };
			addressEl.textContent = "Buscando dirección…";

			const label = await reverseGeocode(c.lat, c.lng);
			// Ignore a stale answer if the map moved on
			if (!this._picker || map.getCenter().lat !== c.lat) return;

			picked.display_name = label
				? [label.name, label.area].filter(Boolean).join(", ")
				: `${c.lat.toFixed(5)}, ${c.lng.toFixed(5)}`;
			addressEl.textContent = picked.display_name;
		};

		map.on("moveend", describeCentre);
		requestAnimationFrame(() => {
			map.invalidateSize();
			describeCentre();
		});

		const close = () => {
			map.remove();
			overlay.remove();
			this._picker = null;
		};

		overlay
			.querySelector(".map-picker__close")
			?.addEventListener("click", close);

		overlay
			.querySelector("#map-picker-confirm")
			?.addEventListener("click", () => {
				if (!isInUS(picked.lat, picked.lng)) {
					addressEl.textContent =
						"Ese punto está fuera de Estados Unidos. Mueve el mapa dentro del país.";
					return;
				}

				this._location = {
					lat: picked.lat,
					lng: picked.lng,
					display_name: picked.display_name || "Ubicación marcada en el mapa",
				};
				this._locationSource = "map";
				close();
				this._renderStep2();
			});
	}

	/* ── Step 3: Photo ─────────────────────────────────────────────────────── */

	_renderStep3() {
		const isICE = this._category?.slug === "ice";

		this._bodyEl.innerHTML = `
          <div class="photo-step">
            <p class="photo-step__hint">Añade una foto (opcional)</p>
            <div class="photo-step__actions">
              <button class="photo-btn" data-action="camera">
                <i class="ph ph-camera" aria-hidden="true"></i>
                Tomar foto
              </button>
              <button class="photo-btn" data-action="gallery">
                <i class="ph ph-image" aria-hidden="true"></i>
                Galería
              </button>
            </div>
            <div class="photo-preview-container"></div>
          </div>
        `;

		// Hidden file inputs
		const cameraInput = document.createElement("input");
		cameraInput.type = "file";
		cameraInput.accept = SUPPORTED_IMAGE_TYPES.join(",");
		cameraInput.capture = "environment";
		cameraInput.style.display = "none";
		cameraInput.addEventListener("change", (e) =>
			this._handlePhotoSelect(e.target.files[0]),
		);
		this._bodyEl.appendChild(cameraInput);

		const galleryInput = document.createElement("input");
		galleryInput.type = "file";
		galleryInput.accept = SUPPORTED_IMAGE_TYPES.join(",");
		galleryInput.style.display = "none";
		galleryInput.addEventListener("change", (e) =>
			this._handlePhotoSelect(e.target.files[0]),
		);
		this._bodyEl.appendChild(galleryInput);

		// Bind buttons
		this._bodyEl
			.querySelector('[data-action="camera"]')
			?.addEventListener("click", () => {
				cameraInput.click();
			});
		this._bodyEl
			.querySelector('[data-action="gallery"]')
			?.addEventListener("click", () => {
				galleryInput.click();
			});

		// Show preview if we have one
		if (this._photoPreview) {
			this._showPhotoPreview();
		}

		// Footer
		this._footerEl.innerHTML = `
      <button class="btn btn--ghost" id="btn-skip-photo">Omitir</button>
      <button class="btn btn--primary" id="btn-next-photo">
        Continuar
      </button>
    `;

		this._footerEl
			.querySelector("#btn-skip-photo")
			?.addEventListener("click", () => {
				// If ICE and photo selected and warning not yet accepted, show warning
				if (isICE && this._photoFile && !this._iceWarningAccepted) {
					this._iceWarning.show();
					return;
				}
				this._goToStep(4);
			});

		this._footerEl
			.querySelector("#btn-next-photo")
			?.addEventListener("click", () => {
				if (isICE && this._photoFile && !this._iceWarningAccepted) {
					this._iceWarning.show();
					return;
				}
				this._goToStep(4);
			});
	}

	_handlePhotoSelect(file) {
		if (!file) return;

		// Avisar AQUÍ y no al publicar: si el formato no sirve, es mejor saberlo
		// mientras se elige la foto que después de rellenar todo el reporte.
		const check = validateImageFile(file);
		if (!check.ok) {
			this._showPhotoError(check.reason);
			return;
		}

		this._photoError = null;
		this._photoFile = file;
		this._photoPreview = URL.createObjectURL(file);
		this._showPhotoPreview();

		// Enable continue button
		const nextBtn = this._footerEl?.querySelector("#btn-next-photo");
		if (nextBtn) nextBtn.disabled = false;
	}

	/**
	 * @param {string} mensaje
	 */
	_showPhotoError(mensaje) {
		this._photoError = mensaje;
		this._photoFile = null;
		this._photoPreview = null;

		const container = this._bodyEl?.querySelector(".photo-preview-container");
		if (container) {
			container.innerHTML = `
        <p class="photo-step__error" role="alert">
          <i class="ph ph-warning-circle" aria-hidden="true"></i>
          ${escapeHtml(mensaje)}
        </p>
      `;
		}

		const nextBtn = this._footerEl?.querySelector("#btn-next-photo");
		if (nextBtn) nextBtn.disabled = true;

		showToast(mensaje, "error");
	}

	_showPhotoPreview() {
		const container = this._bodyEl?.querySelector(".photo-preview-container");
		if (!container) return;

		container.innerHTML = `
      <img class="photo-preview" src="${this._photoPreview}" alt="Vista previa de la foto">
      <button class="btn btn--ghost btn--sm" id="btn-change-photo">Cambiar foto</button>
    `;

		container
			.querySelector("#btn-change-photo")
			?.addEventListener("click", () => {
				this._photoFile = null;
				if (this._photoPreview) URL.revokeObjectURL(this._photoPreview);
				this._photoPreview = null;
				container.innerHTML = "";

				const nextBtn = this._footerEl?.querySelector("#btn-next-photo");
				if (nextBtn) nextBtn.disabled = true;
			});
	}

	/* ── Step 4: Title, Description, Privacy ──────────────────────────────── */

	_renderStep4() {
		const isICE = this._category?.slug === "ice";

		this._bodyEl.innerHTML = `
      <div class="text-step">
        <div class="field">
          <span class="field__label">Título</span>
          <p class="auto-title">
            <span class="auto-title__value">${escapeHtml(categoryTitle(this._category))}</span>
            <span class="auto-title__hint">Lo pone la categoría que elegiste</span>
          </p>
        </div>

        <label class="field">
          <span class="field__label">Descripción</span>
          <textarea class="input input--textarea" id="report-desc"
                    maxlength="${MAX_DESCRIPTION}"
                    placeholder="Más detalles (opcional)">${escapeHtml(this._description)}</textarea>
          <span class="field__counter" id="desc-counter">${this._description.length}/${MAX_DESCRIPTION}</span>
        </label>

        ${
					isICE
						? `
        <fieldset class="privacy-options">
          <legend>Privacidad</legend>
          <label class="toggle-row">
            <span>Ubicación exacta</span>
            <input type="radio" name="precision" value="exact"
                   ${this._locationPrecision === "exact" ? "checked" : ""}>
          </label>
          <label class="toggle-row">
            <span>Ubicación aproximada (~200m)</span>
            <input type="radio" name="precision" value="approx"
                   ${this._locationPrecision === "approx" ? "checked" : ""}>
          </label>
          <label class="toggle-row">
            <span>Nombre real</span>
            <input type="radio" name="display-name" value="real"
                   ${this._displayNameMode === "real" ? "checked" : ""}>
          </label>
          <label class="toggle-row">
            <span>Anónimo</span>
            <input type="radio" name="display-name" value="anonymous"
                   ${this._displayNameMode === "anonymous" ? "checked" : ""}>
          </label>
        </fieldset>
        `
						: ""
				}

        ${this._error ? `<p class="field__error" role="alert">${escapeHtml(this._error)}</p>` : ""}
      </div>
    `;

		// Description binding
		const descInput = this._bodyEl.querySelector("#report-desc");
		const descCounter = this._bodyEl.querySelector("#desc-counter");

		if (descInput) {
			descInput.addEventListener("input", () => {
				// El maxlength del navegador ya corta, pero un pegado desde otra
				// app puede traer más: se recorta también aquí.
				if (descInput.value.length > MAX_DESCRIPTION) {
					descInput.value = descInput.value.slice(0, MAX_DESCRIPTION);
				}
				this._description = descInput.value;

				if (descCounter) {
					const restantes = MAX_DESCRIPTION - this._description.length;
					descCounter.textContent =
						restantes <= AVISO_RESTANTES
							? `Te quedan ${restantes} caracteres`
							: `${this._description.length}/${MAX_DESCRIPTION}`;
					descCounter.classList.toggle(
						"field__counter--warn",
						restantes <= AVISO_RESTANTES,
					);
					descCounter.classList.toggle("field__counter--over", restantes <= 0);
				}
			});
		}

		// Privacy toggles (only for ICE)
		if (isICE) {
			this._bodyEl.querySelectorAll('input[name="precision"]').forEach((r) => {
				r.addEventListener("change", () => {
					if (r.checked) this._locationPrecision = r.value;
				});
			});
			this._bodyEl
				.querySelectorAll('input[name="display-name"]')
				.forEach((r) => {
					r.addEventListener("change", () => {
						if (r.checked) this._displayNameMode = r.value;
					});
				});
		}

		// Footer
		this._footerEl.innerHTML = `
      <button class="btn btn--ghost" id="btn-back-step3">Atrás</button>
      <button class="btn btn--primary ${this._isSubmitting ? "btn--loading" : ""}"
              id="btn-submit-report"
              ${this._category && !this._isSubmitting ? "" : "disabled"}>
        ${this._isSubmitting ? "Publicando..." : "Publicar reporte"}
      </button>
    `;

		this._footerEl
			.querySelector("#btn-back-step3")
			?.addEventListener("click", () => {
				this._goToStep(3);
			});

		const submitBtn = this._footerEl.querySelector("#btn-submit-report");
		if (submitBtn) {
			submitBtn.addEventListener("click", () => this._submit());
		}
	}

	_updateSubmitButton() {
		const btn = this._footerEl?.querySelector("#btn-submit-report");
		if (btn) {
			btn.disabled = !(this._category && !this._isSubmitting);
		}
	}

	/* ── Step 5: Success ───────────────────────────────────────────────────── */

	_renderStep5() {
		this._bodyEl.innerHTML = `
      <div class="success-confirmation">
        <i class="ph ph-check-circle success-icon" aria-hidden="true"></i>
        <h4>Reporte publicado</h4>
        <p>Tu reporte aparecerá en el mapa. Gracias por contribuir.</p>
      </div>
    `;

		this._footerEl.innerHTML = `
      <button class="btn btn--primary btn--block" id="btn-close-success">Cerrar</button>
    `;

		this._footerEl
			.querySelector("#btn-close-success")
			?.addEventListener("click", () => {
				this._close();
			});

		// Update step dots — all done
		const dots = this._stepsEl?.querySelectorAll(".quick-report__step-dot");
		if (dots) {
			for (const dot of dots) {
				dot.classList.add("quick-report__step-dot--done");
				dot.classList.remove("quick-report__step-dot--active");
			}
		}
	}

	/* ── Submit ────────────────────────────────────────────────────────────── */

	async _submit() {
		if (this._isSubmitting || !this._category) return;

		this._isSubmitting = true;
		this._error = null;

		const btn = this._footerEl?.querySelector("#btn-submit-report");
		if (btn) {
			btn.disabled = true;
			btn.textContent = "Publicando...";
		}

		try {
			// Where the user put the report: the address they searched or the
			// point they marked on the map. Only fall back to GPS when they did
			// neither — a chosen location must never be overwritten by the GPS
			// fix, which is what made every report pile up on the user's position.
			let lat;
			let lng;

			if (this._location && this._locationSource !== "gps") {
				lat = this._location.lat;
				lng = this._location.lng;
			} else {
				try {
					const pos = await getCurrentPosition();
					lat = pos.coords.latitude;
					lng = pos.coords.longitude;
				} catch {
					this._error =
						"No se pudo obtener tu ubicación. Busca la dirección o márcala en el mapa.";
					this._isSubmitting = false;
					this._renderStep4();
					return;
				}
			}

			// Coverage check. The offline polygon is only good to a few km, so
			// where possible the country is confirmed against OpenStreetMap —
			// Detroit and Windsor are 2 km apart across the border. If the
			// lookup is unavailable we fall back to the polygon.
			const country = await getCountryCode(lat, lng);
			const outside = country ? country !== "us" : !isInUS(lat, lng);

			if (outside) {
				this._error =
					"Solo se pueden publicar reportes dentro de Estados Unidos. Busca una dirección de EE. UU. o márcala en el mapa.";
				this._isSubmitting = false;
				this._renderStep4();
				return;
			}

			// Fuzz the point for ICE reports asking for approximate location
			if (
				this._category.slug === "ice" &&
				this._locationPrecision === "approx"
			) {
				lat += (Math.random() - 0.5) * 0.0036;
				lng += (Math.random() - 0.5) * 0.0036;
			}

			const location = {
				type: "Point",
				coordinates: [lng, lat],
			};

			// Upload photo if present
			let photoIds = [];
			if (this._photoFile) {
				try {
					const fileId = await uploadPhoto(this._photoFile);
					photoIds = [fileId];
				} catch (err) {
					console.warn(
						"[QuickReport] Photo upload failed, continuing without photo:",
						err,
					);
					showToast(
						`${err?.message || "La foto no se pudo subir"} — el reporte se publicará sin ella.`,
						"warning",
					);
				}
			}

			// Build payload
			const payload = {
				// El título ya no se escribe a mano: es la categoría con su emoji
				title: categoryTitle(this._category),
				description:
					this._description.trim().slice(0, MAX_DESCRIPTION) || undefined,
				category: this._category.id,
				location,
				location_precision: this._locationPrecision,
				display_name_mode: this._displayNameMode,
			};

			// POST report
			const result = await createReport(payload);

			// Las fotos van en su propia colección (report_photos): enviarlas
			// dentro del payload no hacía nada, el campo no existe en `reports`.
			if (photoIds.length > 0 && result?.id) {
				try {
					await linkReportPhotos(result.id, photoIds);
				} catch (err) {
					console.warn("[QuickReport] No se pudo enlazar la foto:", err);
					showToast(
						"El reporte se publicó, pero la foto no se pudo adjuntar.",
						"warning",
					);
				}
			}

			// Dispatch submit event
			this.dispatchEvent(
				new CustomEvent("quick-report:submit", {
					detail: { reportPayload: payload, result },
					bubbles: true,
				}),
			);

			// Show success
			showToast("Reporte publicado con éxito", "success");
			this._goToStep(5);
		} catch (err) {
			console.error("[QuickReport] Submit error:", err);
			this._error =
				err.message || "Error al publicar el reporte. Intenta de nuevo.";
			this._isSubmitting = false;
			this._renderStep4();
		}
	}
}

/* ── Helpers ─────────────────────────────────────────────────────────────── */

function getCurrentPosition() {
	return new Promise((resolve, reject) => {
		if (!navigator.geolocation) {
			reject(new Error("Geolocation not supported"));
			return;
		}
		navigator.geolocation.getCurrentPosition(resolve, reject, {
			enableHighAccuracy: true,
			timeout: 10000,
			maximumAge: 60000,
		});
	});
}

function escapeHtml(str) {
	const div = document.createElement("div");
	div.textContent = str;
	return div.innerHTML;
}

function escapeAttr(str) {
	return (str || "").replace(/"/g, "&quot;").replace(/'/g, "&#39;");
}

customElements.define("quick-report-sheet", QuickReportSheet);
